import _ from 'underscore';
import { useContext, useMemo } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import type { CMUICurrentUser, CMUIServerContext } from '@bingads-webui-campaign-react/server-context';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { useAccountFlights, FLIGHT_STATUS } from '@bingads-webui-campaign-react/campaign-flights';

export interface PilotServerContext extends Partial<Pick<CMUIServerContext, 'Permissions' | 'DynamicPermissions'>> {
  CCUIPilots?: Record<string, boolean | Record<string, boolean>>;
  CurrentUser?: Partial<Pick<CMUICurrentUser, 'IsInternal' | 'EnforcementActionId'>>;
  IsInternal?: boolean;
}

export const getPilotValueWithSignUp = (serverContext: PilotServerContext | null | undefined, key?: string) => {
  const ccuiPilotKey = key && key.replace(/^Is/, 'is');
  const ccDynamicPilotKey = key && key.replace(/^(is|Is)/, '').replace(/Enabled$/, '');
  // CCUIPilots only return by signup page
  const ccuiPilotValue = _.get(serverContext, ['CCUIPilots', ccuiPilotKey], false);
  const ccuiPilotValueWithDynamic = _.get(serverContext, ['CCUIPilots', 'dynamic', ccDynamicPilotKey], false);

  return ccuiPilotValue || ccuiPilotValueWithDynamic;
};

export const getPilotValue = (serverContext: PilotServerContext | null | undefined, key?: string) => {
  const cmuiPilotKey = key && key.replace(/^is/, 'Is');
  const uflPilotKey = key && key.replace(/^(is|Is)/, '').replace(/Enabled$/, ''); // ufl stands for Unified Feature Library

  const cmuiPilotValue = _.get(serverContext, ['Permissions', cmuiPilotKey], false);
  const uflPilotValue = _.get(serverContext, ['DynamicPermissions', uflPilotKey], false);

  const signupPilotValue = getPilotValueWithSignUp(serverContext, key);

  return cmuiPilotValue || uflPilotValue || signupPilotValue;
};

/**
 * Hook to check if a pilot/feature flag is enabled
 * @param {string} key - The pilot key to check
 * @param {boolean} onlySignup - If true, only check signup-specific pilots
 * @param {boolean} includeNewAccountFlights - If true, also check flight allocations using key as treatmentFlightName
 * @param {boolean} autoLog - If false, disable auto log and return logFlight callback instead
 * @returns {boolean} Whether the pilot is enabled (via pilot flag OR flight allocation)
 */
export function usePilotsValue(key?: string, onlySignup = false, includeNewAccountFlights = false, autoLog = true) {
  const serverContext = useContext(ServerContext);
  // @aiContributed-MAPFuse-2026-03-05
  const isPilotEnabled = useMemo(
    () => {
      const getPilot = onlySignup ? getPilotValueWithSignUp : getPilotValue;
      return key ? getPilot(serverContext, key) : false;
    },
    [key, serverContext, onlySignup]
  );

  const flightNameBase = key ? key.replace(/^(is|Is)/, '').replace(/Enabled$/, '') : '';
  const flightsResult = useAccountFlights({
    treatmentFlightName: `${flightNameBase}_treatment`,
    controlFlightName: `${flightNameBase}_control`,
    autoLog: includeNewAccountFlights && autoLog,
  });

  const value = isPilotEnabled || (includeNewAccountFlights && flightsResult.flightStatus === FLIGHT_STATUS.IN_TREATMENT);

  // When autoLog is disabled and includeNewAccountFlights is true, expose logFlight so the
  // caller can manually trigger the traffic-split exposure log at the right moment (e.g. on click).
  if (includeNewAccountFlights && autoLog === false) {
    return { value, logFlight: flightsResult.logFlight };
  }

  return value;
}

export const useIsInternalUser = () => {
  const serverContext = useContext(ServerContext);
  if (serverContext.CurrentUser && !_.isUndefined(serverContext.CurrentUser.IsInternal)) {
    return serverContext.CurrentUser.IsInternal;
  }
  return serverContext.IsInternal || false;
};

export const useIsReadOnlyUser = () => {
  const isReadOnly = usePilotsValue('IsReadOnlyUser');

  const serverContext = useContext(ServerContext);
  const enforcementActionId = _.get(serverContext, ['CurrentUser', 'EnforcementActionId']);
  const isDSAReadOnly = enforcementActionId === Constants.EnforcementActionType.ReadOnly;

  return isReadOnly || isDSAReadOnly;
};
